import {createElement, type ReactNode} from "react";
import type {
	FullCourseLevel,
	CoursePageSection,
} from "@repo/sanity-utils/query-types";
import type {CampusLocation} from "@repo/actions/AcademyApi/GetCampuses";
import {CampusLocationSelector} from "@/app/components/CourseLevelPage/CampusLocationSelector/CampusLocationSelector";
import {getKeyedCopyText} from "@repo/utils/keyedCopy";
import {getUnavailableCampusPriceLines} from "./learningFormatAvailability";
import {
	resolveInPersonBillingType,
	type InPersonBillingType,
} from "./learningFormatData";
import {formatOfferingPrice, getOfferingPrice} from "./offeringPrice";

const STARTING_AT_KEY = "starting-at";
const FALLBACK_STARTING_AT = "Starting at";
const BILL_FREQUENCY_SEQUENTIAL_KEY = "bill-frequency-sequential";
const FALLBACK_BILL_FREQUENCY_SEQUENTIAL =
	"Tuition {{totalPrice}}, billed per half.";
const BILL_FREQUENCY_IN_PERSON_KEY = "bill-frequency-in-person";
const FALLBACK_BILL_FREQUENCY_IN_PERSON =
	"Tuition {{totalPrice}}, billed per trimester.";
const BILL_FREQUENCY_DEFAULT_KEY = "bill-frequency-default";
const FALLBACK_BILL_FREQUENCY_DEFAULT = "Billed once at enrollment.";
const PRICE_SEQUENTIAL_KEY = "price-sequential";
const FALLBACK_PRICE_SEQUENTIAL = "{{price}}/half";
const PRICE_IN_PERSON_KEY = "price-in-person";
const FALLBACK_PRICE_IN_PERSON = "{{price}}/trimester";
const PRICE_DEFAULT_KEY = "price-default";
const FALLBACK_PRICE_DEFAULT = "{{price}}";

/** Sequential courses bill once per half (Part 1 + Part 2). */
const SEQUENTIAL_BILLING_PERIODS = 2;
/** In-person academic-year courses bill once per trimester. */
const IN_PERSON_BILLING_PERIODS = 3;

/**
 * Format the secondary price line that shows total tuition across billing periods.
 */
function formatTotalTuitionSecondary(
	unitPrice: number,
	billingPeriods: number,
	keyedCopy: {key: string; text: string}[],
	key: string,
	fallback: string
): string {
	const totalPrice = formatOfferingPrice(unitPrice * billingPeriods);
	return (getKeyedCopyText(keyedCopy, key) || fallback).replace(
		"{{totalPrice}}",
		totalPrice
	);
}

type Modality = NonNullable<
	NonNullable<FullCourseLevel>["modalityOfferings"]
>[number]["modality"];

type CourseOffering = NonNullable<FullCourseLevel>["courseOfferings"][number];

/**
 * Resolve the min/max price for course offerings, preferring a matching
 * campus `coursePrice` when the display mode is `campusLocation` and
 * `selectedCampusId` is set.
 */
function getOfferingPriceRange(
	offerings: CourseOffering[],
	displayMode: string | null | undefined,
	selectedCampusId: string | null,
	campusCoursePrices: Readonly<Record<string, number>> = {}
): {minPrice: number; maxPrice: number} | null {
	const prices = offerings
		.map((offering) =>
			getOfferingPrice(
				offering,
				displayMode,
				selectedCampusId,
				campusCoursePrices
			)
		)
		.filter((p): p is number => p !== null && p > 0);

	// I don't know if we'll ever have classes with no price, but still check for it anyway
	if (!prices.length) {
		return null;
	}

	return {
		minPrice: Math.min(...prices),
		maxPrice: Math.max(...prices),
	};
}

/**
 * Get the price range of course offerings for a given modality and format the price lines for display.
 *
 * Multi-billing modalities show unit price as primary and total tuition as secondary:
 * - sequential → primary `{{price}}/half`, secondary `Tuition {{totalPrice}}, billed per half.`
 * - campusLocation trimester → primary price, secondary `Tuition {{totalPrice}}, billed per trimester.`
 * - campusLocation once / otherwise → primary price, secondary `Billed once at enrollment.`
 *
 * When `displayMode` is `campusLocation` and no campus is selected, the campus
 * location selector is shown as the primary line. Once a campus is selected,
 * the selector moves to tertiary (rendered above the price stack) and
 * primary/secondary show the price so four-column prices stay aligned.
 *
 * @param modality
 * @param courseOfferings
 * @param sequenceType
 * @param selectedCampusId
 * @param campuses
 * @param campusCoursePrices - slug → coursePrice for offerings with no campus list
 * @param keyedCopy - learning-format coursePageSection keyed copy
 * @param campusLocationCoursePageSection - campus-location-selector section
 * @param inPersonBillingType - trimester (default) or once for PC courses
 * @param courseLevelSlug - course level slug for discovery analytics on campus confirm
 * @returns
 */
export function formatModalityPriceLines(
	modality: Modality,
	courseOfferings: NonNullable<FullCourseLevel>["courseOfferings"],
	sequenceType: NonNullable<FullCourseLevel>["sequenceType"],
	selectedCampusId: string | null,
	campuses: CampusLocation[] = [],
	campusCoursePrices: Readonly<Record<string, number>> = {},
	keyedCopy: {key: string; text: string}[] = [],
	campusLocationCoursePageSection?: NonNullable<CoursePageSection>,
	inPersonBillingType: InPersonBillingType | null | undefined = "trimester",
	courseLevelSlug: string | null | undefined = null
): {
	primary: ReactNode;
	secondary: ReactNode;
	tertiary: ReactNode;
	isUnavailable?: boolean;
} | null {
	const modalityId = modality?._id;
	const modalityName = modality?.title ?? "";
	const modalitySlug = modality?.slug?.current ?? null;

	// If there is no modality, just return early
	if (!modalityId) {
		return null;
	}
	const {displayMode, header, subheader} = modality.price ?? {};

	// Define the response object
	const response: {
		primary: ReactNode;
		secondary: ReactNode;
		tertiary: ReactNode;
	} = {
		primary: null,
		secondary: null,
		tertiary: null,
	};

	if (displayMode === "manual") {
		response.primary = header;
		response.secondary = subheader;
		return response;
	}

	// Find the course offerings for the given modality
	const matches = courseOfferings.filter((o) => o.modality?._id === modalityId);
	if (!matches.length) {
		return null;
	}

	// Prefer createElement over JSX so vitest/rolldown can parse this module
	// without a React JSX transform (aops lib tests are node-side).
	const offeringCampusIds = [
		...new Set(
			matches
				.flatMap((offering) => offering.campuses ?? [])
				.map((campus) => campus.slug?.current)
				.filter((id): id is string => Boolean(id))
		),
	];
	const campusLocationSelector = createElement(CampusLocationSelector, {
		campuses,
		modalityName,
		coursePageSection: campusLocationCoursePageSection,
		modalityId,
		modalitySlug,
		courseLevelSlug: courseLevelSlug ?? null,
		offeringCampusIds,
	});

	const unavailablePriceLines = getUnavailableCampusPriceLines(
		matches,
		selectedCampusId,
		keyedCopy
	);
	if (unavailablePriceLines) {
		return {
			...unavailablePriceLines,
			tertiary: campusLocationSelector,
		};
	}

	// Prompt for a campus before showing price
	if (displayMode === "campusLocation" && !selectedCampusId) {
		return {
			primary: campusLocationSelector,
			secondary: subheader,
			tertiary: null,
		};
	}

	// Get the price range for the course offerings
	const prices = getOfferingPriceRange(
		matches,
		modality.price?.displayMode,
		selectedCampusId,
		campusCoursePrices
	);
	if (!prices) {
		return null;
	}
	const {minPrice, maxPrice} = prices;

	// Display the starting at text if the price is not the same for all offerings
	const startingAt =
		getKeyedCopyText(keyedCopy, STARTING_AT_KEY) || FALLBACK_STARTING_AT;
	const pricePrefix = minPrice === maxPrice ? "" : `${startingAt} `;

	// Format the price
	const price = formatOfferingPrice(minPrice);

	// If the display mode is campus location, show the price and the campus location selector
	if (displayMode === "campusLocation") {
		// Get price display for trimester billing
		const trimesterPrice = (
			getKeyedCopyText(keyedCopy, PRICE_IN_PERSON_KEY) ||
			FALLBACK_PRICE_IN_PERSON
		).replace("{{price}}", price);

		response.primary = `${pricePrefix}${trimesterPrice}`;
		response.secondary =
			resolveInPersonBillingType(inPersonBillingType) === "once"
				? getKeyedCopyText(keyedCopy, BILL_FREQUENCY_DEFAULT_KEY) ||
					FALLBACK_BILL_FREQUENCY_DEFAULT
				: formatTotalTuitionSecondary(
						minPrice,
						IN_PERSON_BILLING_PERIODS,
						keyedCopy,
						BILL_FREQUENCY_IN_PERSON_KEY,
						FALLBACK_BILL_FREQUENCY_IN_PERSON
					);
		response.tertiary = campusLocationSelector;
	} else if (sequenceType === "sequential") {
		// Get price display for sequential billing
		const sequentialPrice = (
			getKeyedCopyText(keyedCopy, PRICE_SEQUENTIAL_KEY) ||
			FALLBACK_PRICE_SEQUENTIAL
		).replace("{{price}}", price);

		// If the sequence type is sequential, show the price and the secondary text
		response.primary = `${pricePrefix}${sequentialPrice}`;
		response.secondary = formatTotalTuitionSecondary(
			minPrice,
			SEQUENTIAL_BILLING_PERIODS,
			keyedCopy,
			BILL_FREQUENCY_SEQUENTIAL_KEY,
			FALLBACK_BILL_FREQUENCY_SEQUENTIAL
		);
	} else {
		// Get price display for default billing
		const defaultPrice = (
			getKeyedCopyText(keyedCopy, PRICE_DEFAULT_KEY) || FALLBACK_PRICE_DEFAULT
		).replace("{{price}}", price);

		// If the sequence type is default, show the price and the secondary text
		response.primary = `${pricePrefix}${defaultPrice}`;
		response.secondary =
			getKeyedCopyText(keyedCopy, BILL_FREQUENCY_DEFAULT_KEY) ||
			FALLBACK_BILL_FREQUENCY_DEFAULT;
	}

	return response;
}
